import { useEffect, useMemo } from 'react';
import { updateWorkspaceView, WorkspaceAccessResponse } from '../api/workspace';
import { Filter } from '../components/Modal/TaskFiltersModal';
import { isFiltersSelectionEqual, updateCurrentFilters } from '../components/TaskManagement/helpers/filterHelpers';
import { useGetWorkspace } from '../serverState/workspace';
import { TaskStatus, defaultTaskStatuses } from '../types/TaskStatus';
import { useCurrentWorkspaceEpics } from '../serverState/epics';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from '../types/WorkspaceView';
import { useViewContext } from '../pages/Space/Context/ViewContext';
import { queryClient } from '../queryClient';
import Workspace, { WorkspaceFramework } from '../types/Workspace';
import { useFilterStore } from '../stores/filterStore';
import { clearFiltersFromLocalStorage } from '../common/localStorage';

export const useTaskFilterManagement = (
  activeView: WorkspaceView,
  usersWithAccessToSpace: WorkspaceAccessResponse[],
  onSaveForAllSuccess: () => void
) => {
  const workspaceQuery = useGetWorkspace();
  const epicsQuery = useCurrentWorkspaceEpics();
  const viewContext = useViewContext();

  const statusOptions = useMemo(
    () => (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses,
    [workspaceQuery.data?.TaskStatus]
  );

  const viewFilters = Array.isArray(activeView?.filters) ? activeView?.filters : null;

  // Zustand store actions and selectors
  const { initializeFilters, setFilters, getFilters, getSelectedFilters, resetFilters } = useFilterStore();

  const filters = getFilters(activeView.workspaceId, activeView.id);
  const selectedFilters = getSelectedFilters(activeView.workspaceId, activeView.id);

  const isViewSelectedFilters = useMemo(() => {
    // Only compare filter types available for the current entity type.
    // Epic view has fewer filter types than task view, so saved task-specific
    // filters (e.g. status) should not trigger a false dirty state.
    const availableFilterTypes = new Set(filters.map((f) => f.type));

    const taskStatuses = workspaceQuery.data?.TaskStatus;

    const applicableViewFilters =
      viewFilters
        ?.filter((filter) => availableFilterTypes.has(filter.type))
        .map((filter) => {
          if (filter.type === 'status' && taskStatuses?.length) {
            return {
              ...filter,
              options:
                filter.options?.filter((option) =>
                  taskStatuses.some((status: any) => status.id === option.metadata?.id)
                ) ?? [],
            };
          }
          return filter;
        }) ?? [];

    return isFiltersSelectionEqual(selectedFilters, applicableViewFilters);
  }, [selectedFilters, viewFilters, filters]);

  const epicFilter = useMemo(() => selectedFilters.find((filter) => filter.type === 'epic'), [selectedFilters]);

  const ownerFilter = useMemo(() => {
    return filters.find((filter) => filter.type === 'owner');
  }, [filters]);

  const statusFilter = useMemo(() => {
    return filters.find((filter) => filter.type === 'status');
  }, [filters]);

  const deadlineFilter = useMemo(() => {
    return filters.find((filter) => filter.type === 'deadline');
  }, [filters]);

  const priorityFilter = useMemo(() => {
    return filters.find((filter) => filter.type === 'priority');
  }, [filters]);

  const estimateFilter = useMemo(() => {
    return filters.find((filter) => filter.type === 'estimate');
  }, [filters]);

  // Initialize filters when dependencies are ready
  useEffect(() => {
    if (epicsQuery.data && usersWithAccessToSpace && workspaceQuery.data) {
      initializeFilters(
        activeView.workspaceId,
        activeView.id,
        epicsQuery.data,
        usersWithAccessToSpace.map((user) => user.User),
        workspaceQuery.data.TaskStatus || statusOptions,
        workspaceQuery.data.Labels || [],
        workspaceQuery.data.estimationType,
        workspaceQuery.data.framework || WorkspaceFramework.none,
        activeView.group || WorkspaceViewGroup.backlog,
        activeView.entityType || WorkspaceViewEntityType.task,
        viewFilters || undefined
      );
    }
  }, [
    activeView.workspaceId,
    activeView.id,
    activeView.group,
    activeView.entityType,
    epicsQuery.data,
    usersWithAccessToSpace,
    workspaceQuery.data,
    statusOptions,
    viewFilters,
    initializeFilters,
  ]);

  // Update filters when data changes (but don't reinitialize)
  useEffect(() => {
    if (filters.length > 0 && epicsQuery.data && usersWithAccessToSpace.length > 0 && workspaceQuery.data) {
      const updatedFilters = updateCurrentFilters(
        filters,
        epicsQuery.data,
        usersWithAccessToSpace.map((user) => user.User),
        workspaceQuery.data.TaskStatus || statusOptions,
        workspaceQuery.data.Labels || []
      );

      // Only update if filters actually changed
      if (JSON.stringify(updatedFilters) !== JSON.stringify(filters)) {
        setFilters(activeView.workspaceId, activeView.id, updatedFilters);
      }
    }
  }, [epicsQuery.data, usersWithAccessToSpace, workspaceQuery.data?.TaskStatus, workspaceQuery.data?.Labels]);

  const handleSaveForAll = async (newFilters: Filter[]) => {
    // Update the store
    setFilters(activeView.workspaceId, activeView.id, newFilters);

    // Get only the filters with selected options.
    // #2610: clone — the saved view and the workspace cache must not share Filter
    // objects with the live filter store, which is re-selected in place as the user
    // ticks options.
    const filtersToSave = structuredClone(
      newFilters.filter((filter) => {
        const selectedOptions = filter.options?.filter((option) => option.selected) ?? [];
        return selectedOptions.length > 0;
      })
    );

    const res = await updateWorkspaceView(activeView.workspaceId, activeView.id, {
      filters: filtersToSave,
    });

    if (res.status === 200) {
      // Clear localStorage so local state matches the server-saved state
      clearFiltersFromLocalStorage(activeView.workspaceId, activeView.id);

      viewContext?.setView({
        ...activeView,
        filters: filtersToSave,
      });

      queryClient.setQueryData(['workspace', Number(activeView.workspaceId)], (oldData: Workspace) => {
        return {
          ...oldData,
          Views: oldData.Views.map((view) => {
            if (view.id === activeView.id) {
              return {
                ...view,
                filters: filtersToSave,
              };
            }
            return view;
          }),
        };
      });

      onSaveForAllSuccess();
    }
  };

  const handleResetToSaved = () => {
    if (epicsQuery.data && usersWithAccessToSpace && workspaceQuery.data) {
      resetFilters(
        activeView.workspaceId,
        activeView.id,
        epicsQuery.data,
        usersWithAccessToSpace.map((user) => user.User),
        workspaceQuery.data.TaskStatus || statusOptions,
        workspaceQuery.data.Labels || [],
        workspaceQuery.data.estimationType,
        workspaceQuery.data.framework || WorkspaceFramework.none,
        activeView.group || WorkspaceViewGroup.backlog,
        activeView.entityType || WorkspaceViewEntityType.task,
        viewFilters || undefined
      );
    }
  };

  return {
    filters,
    setFilters: (newFilters: Filter[]) => setFilters(activeView.workspaceId, activeView.id, newFilters),
    selectedFilters,
    isViewSelectedFilters,
    epicFilter,
    ownerFilter,
    statusFilter,
    deadlineFilter,
    priorityFilter,
    estimateFilter,
    handleSaveForAll,
    handleResetToSaved,
  };
};
